import { useApolloClient } from '@apollo/client'
import type { RefresherEventDetail } from '@ionic/react'
import { IonContent, IonRefresher, IonRefresherContent, useIonRouter } from '@ionic/react'
import clsx from 'clsx'
import { forwardRef, useCallback, useLayoutEffect, useState } from 'react'

import { WhoamiDocument } from '../../lib/apollo/types'
import { useUser } from '../../providers/Auth/hooks'
import { showsTabBar } from '../TabBar'

import './style.scss'

interface ContentProps {
    /**
     * Shows the pull-to-refresh even on pages without a tab bar. Certificates
     * arrive in the background, so the list needs a way to ask again without
     * leaving the page.
     */
    withRefresher?: boolean
}

export type ContentComponentProps = React.ComponentProps<typeof IonContent> & ContentProps

const Content = forwardRef<HTMLIonContentElement | null, ContentComponentProps>(({ children, withRefresher = false, ...props }, ref) => {
    const [hideTabBar, setHideTabBar] = useState(true)
    const { routeInfo: { pathname } } = useIonRouter()

    useLayoutEffect(() => {
        setHideTabBar(!showsTabBar(pathname))
    }, [pathname])

    const client = useApolloClient()
    const user = useUser()

    const handleRefresh = useCallback(async (event: CustomEvent<RefresherEventDetail>) => {
        const savedUser = { ...user }
        client.onResetStore(async () => {
            client.writeQuery({
                query: WhoamiDocument,
                data: { whoami: savedUser },
            })
        })
        try {
            await client.resetStore()
        } catch (e) {
            // do nothing
        }
        setTimeout(() => {
            event.detail.complete()
        }, 600)
    }, [user, client])

    return (
        <IonContent
            ref={ref}
            {...props}
            className={clsx('content', props.className, { 'content--with-tab-bar': !hideTabBar })}
        >
            {(!hideTabBar || withRefresher) && (
                <IonRefresher
                    slot='fixed'
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        refreshingSpinner='circular'
                        color='secondary'
                    />
                </IonRefresher>
            )}

            { children }
        </IonContent>
    )
})

Content.displayName = 'Content'

export default Content
